import { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { AppType } from "@core/types/app.types";
import { ThemeType } from "@core/types/style.types";
import { Type } from "@themes/pollinator/ui/Type";
import { ActionButtonsGroup } from "@themes/pollinator/ui/ActionButtonsGroup";

export type Props = {
  /** Call to action title */
  title: ReactNode;
  /** Optional description */
  description?: string;
  /** Optional children rendered between description and action buttons */
  children?: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Additional CSS classes for the title */
  titleClassName?: string;
  /** Theme variant */
  theme?: ThemeType;
  /** App variant (affects typography sizes) */
  variant?: AppType;
  /** Secondary button click handler */
  onSecondaryClick?: () => void;
  /** Primary button click handler */
  onPrimaryClick?: () => void;
  /** Secondary button text */
  secondaryButtonText?: string;
  /** Primary button text */
  primaryButtonText?: string;
};

/**
 * A call to action component with title, description, and action buttons.
 */
export const CallToAction = ({
  title,
  description,
  children,
  className,
  titleClassName,
  theme = "dark",
  variant = "platform",
  onSecondaryClick,
  onPrimaryClick,
  secondaryButtonText,
  primaryButtonText,
}: Props) => {
  return (
    <div
      className={twMerge(
        "flex flex-col flex-wrap px-4 pt-6 pb-4 rounded-lg gap-8 md:p-8 md:flex-row md:justify-between",
        theme === "light" &&
          "bg-brand-50 text-brand-800 border border-brand-800",
        theme === "dark" && "bg-brand-800 text-brand-50",
        className
      )}
      data-testid="call-to-action"
    >
      <div
        className={twMerge(
          "flex flex-col",
          variant === "platform" && "gap-6",
          variant === "portal" && "gap-3"
        )}
      >
        <Type
          variant={variant === "platform" ? "h3" : "h4"}
          className={twMerge("max-w-3xl", titleClassName)}
        >
          {title}
        </Type>
        {description && (
          <Type variant={variant === "platform" ? "body1" : "body2"} className="max-w-4xl">
            {description}
          </Type>
        )}
        {children}
      </div>
      <ActionButtonsGroup
        theme={theme}
        onSecondaryClick={onSecondaryClick}
        onPrimaryClick={onPrimaryClick}
        secondaryButtonText={secondaryButtonText}
        primaryButtonText={primaryButtonText}
        primaryButtonProps={{
          className: "w-full md:w-auto",
        }}
      />
    </div>
  );
};

export default CallToAction;
